// src/components/RuleChips.tsx
import React from 'react';
import { RuleGroup } from '@app/react-common/components/rule-engine-v2/models/RuleGroup';
import { Rule } from '@app/react-common/components/rule-engine-v2/models/Rule';
import {
  getAbTestRuleAsDescription,
  getCountryRuleTextDescription,
  mapRuleEngineApiDtoToRuleEngineState,
  mapCheckoutSegmentToRuleEngineState,
} from '../rule-engine-v2/utils/ruleEngineMapper';
import { RuleEngineApiDto } from '@app/pages/checkout-app/checkout-layout/main/components/ab-testing/react/models/AbTestRulesApiDto';
import Tooltip from '../tooltip/Tooltip';

interface RuleChipsProps {
  abTestRules?: RuleEngineApiDto | null;
  segmentedRuleGroup?: RuleGroup | null;
}

export const RuleChips: React.FC<RuleChipsProps> = ({ abTestRules, segmentedRuleGroup }) => {
  if (!abTestRules && !segmentedRuleGroup) {
    return null;
  }
  const ruleEngineState = segmentedRuleGroup
    ? mapCheckoutSegmentToRuleEngineState(segmentedRuleGroup)
    : mapRuleEngineApiDtoToRuleEngineState(abTestRules);
  const { matchAllRuleGroups, ruleGroups } = ruleEngineState;

  return (
    <div className="rule-chips-container">
      {ruleGroups.map((group: RuleGroup, groupIndex: number) => (
        <React.Fragment key={groupIndex}>
          {/** 1) Render this group’s chips (with inline connectors) **/}
          <div className="rule-group-row">
            {group.rules.map((rule: Rule, ruleIndex: number) => {
              // Decide which text‐builder to use:
              const text = getAbTestRuleAsDescription(rule);
              const isCountriesRule =
                rule.property.toLowerCase() === 'countries' || rule.property.toLowerCase() === 'destinations';
              const ruleChip = <label className="rule-chip">{text}</label>;

              return (
                <React.Fragment key={`group-${groupIndex}-rule-${ruleIndex}`}>
                  {isCountriesRule ? (
                    <Tooltip appearOnSide="top" tooltipText={getCountryRuleTextDescription(rule)}>
                      {ruleChip}
                    </Tooltip>
                  ) : (
                    ruleChip
                  )}
                  {ruleIndex < group.rules.length - 1 && (
                    <label className="rule-internal-connector">{group.matchAllRules ? 'and' : 'or'}</label>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/** 2) If this is not the last group, render the group‐level connector on its own line **/}
          {groupIndex < ruleGroups.length - 1 && (
            <label className="rule-connector-row">{matchAllRuleGroups ? 'and' : 'or'}</label>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
